/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { useMaximize, MaxButton } from "../pdf/MaxToggle";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { FileEdit, Download, MessageSquare, Mail, Send, FileText } from "lucide-react";
import { fmtMoney } from "../../lib/appUtils";
import { fmtD } from "../../lib/date";
import { CollectionFollowupStatus, CollectionDeliveryResult, collectionChannelBlock, collectionError, deliverySummary, newCollectionRequestId, resultLocks } from "./CollectionFollowupStatus";

const inp = "mt-1 w-full text-sm rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-300";

/* 变更单一键催收：变更单 Invoice PDF（含汇款信息）+ 中英话术 + 短信 / 邮件 → 发送并记入收款沟通记录；收款仍由财务在变更订单页确认。
   project：财务「项目财务 · 收款计划」入口（同一后台 /finance/project/{pid}）。 */
export function CoCollectDialog({ api, leadId, ids, zh, onClose, onSent, project }) {
  const [pv, setPv] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [subject, setSubject] = useState("");
  const [ch, setCh] = useState({ sms: false, email: false });
  const [pdfUrl, setPdfUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [locks, setLocks] = useState({});
  const requestId = useRef(newCollectionRequestId());
  const pdfObjectUrl = useRef("");
  const previewGeneration = useRef(0);
  const q = ids?.length ? `?ids=${ids.join(",")}` : "";
  const base = project ? `/finance/project/${project}` : `/crm/signed/${leadId}`;
  const mx = useMaximize();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`${base}/co-collect-preview${q}`);
        if (cancelled) return;
        setPv(data); setMsg(data.sms || ""); setSubject(data.subject || "");
        setCh({ sms: !!data.channels?.sms && !!data.channels?.smsConfigured && !collectionChannelBlock(data.items, "sms", zh),
          email: !!data.channels?.email && !!data.channels?.emailConfigured && !collectionChannelBlock(data.items, "email", zh) });
        const r = await api.get(`${base}/co-invoice.pdf${q}`, { responseType: "blob" });
        if (cancelled) return;
        if (pdfObjectUrl.current) URL.revokeObjectURL(pdfObjectUrl.current);
        pdfObjectUrl.current = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        setPdfUrl(pdfObjectUrl.current);
      } catch (e) { setErr(collectionError(e, zh)); }
    })();
    return () => { cancelled = true; previewGeneration.current += 1; if (pdfObjectUrl.current) URL.revokeObjectURL(pdfObjectUrl.current); pdfObjectUrl.current = ""; };
  }, [api, base, q, zh]);

  const blocked = k => locks[k] || collectionChannelBlock(pv?.items, k, zh);
  const refreshStatus = async () => {
    const generation = previewGeneration.current;
    const { data } = await api.get(`${base}/co-collect-preview${q}`);
    if (generation !== previewGeneration.current) return data;
    setPv(data);
    if (data.previewVersion !== pv?.previewVersion) {
      setMsg(data.sms || ""); setSubject(data.subject || "");
      setCh({ sms: false, email: false });
      if (pdfObjectUrl.current) URL.revokeObjectURL(pdfObjectUrl.current);
      pdfObjectUrl.current = ""; setPdfUrl("");
      const r = await api.get(`${base}/co-invoice.pdf${q}`, { responseType: "blob" });
      if (generation !== previewGeneration.current) return data;
      pdfObjectUrl.current = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPdfUrl(pdfObjectUrl.current);
    }
    return data;
  };

  const download = () => { const a = document.createElement("a"); a.href = pdfUrl; a.download = `CO_Invoice_${pv?.invoiceNumbers || leadId || project}.pdf`.replace(/[, ]+/g, "_"); a.click(); };
  const send = async () => {
    const channels = Object.keys(ch).filter((k) => ch[k] && !blocked(k));
    if (!channels.length) { toast.error(zh ? "请至少选择一个发送通道" : "Select at least one channel"); return; }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`${base}/co-collect-send`, { ids: pv.items.map((p) => p.coId), channels, message: msg, subject, previewVersion: pv?.previewVersion, requestId: requestId.current });
      setDone(data);
      const nextLocks = resultLocks(locks, data, zh); setLocks(nextLocks);
      const summary = deliverySummary(data, zh); toast[summary.complete ? "success" : "warning"](summary.text);
      requestId.current = newCollectionRequestId();
      setCh({ sms: channels.includes("sms") && !nextLocks.sms, email: channels.includes("email") && !nextLocks.email });
      try { await refreshStatus(); } catch { setErr(zh ? "发送结果已记录，最新状态加载失败，请刷新核对。" : "Delivery recorded; refresh to load the latest status."); }
      onSent?.();
    } catch (e) {
      const message = collectionError(e, zh); setErr(message); toast.error(message);
      if (e?.response?.status === 409) {
        try { await refreshStatus(); setCh({ sms: false, email: false }); } catch { /* 保留错误，由用户刷新后核对。 */ }
      }
    }
    setBusy(false);
  };

  const channel = ({ k, icon: Icon, label, to, avail, configured }) => (
    <label className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm min-h-11 ${!avail || !configured || blocked(k) ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-slate-400"} ${ch[k] && !blocked(k) ? "border-brand-300 bg-brand-50/40" : "border-slate-200"}`} data-testid={`co-collect-ch-${k}`} data-on={ch[k] && !blocked(k) ? 1 : 0} data-avail={avail ? 1 : 0}>
      <input type="checkbox" className="accent-brand-600" disabled={busy || !avail || !configured || !!blocked(k)} checked={ch[k] && !blocked(k)} onChange={(e) => setCh({ ...ch, [k]: e.target.checked })} data-testid={`co-collect-ch-${k}-input`} />
      <Icon className="h-4 w-4 text-slate-500" />
      <span className="font-medium text-slate-800">{label}</span>
      <span className="ml-auto text-xs text-slate-500 break-words">{blocked(k) || (!avail ? (zh ? "客户无此联系方式" : "No contact on file") : !configured ? (zh ? "服务未配置" : "Not configured") : to)}</span>
    </label>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className={mx.contentCls("max-w-5xl max-h-[92dvh] overflow-y-auto")} data-max={mx.max ? 1 : 0} data-testid="co-collect-dialog" data-lead={leadId || ""} data-project={project || ""}>
        <MaxButton max={mx.max} onToggle={mx.toggle} zh={zh} testId="co-collect-max" />
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-indigo-700"><FileEdit className="h-5 w-5" />{zh ? "预览变更单 Invoice · 发送给客户（短信 / 邮件）" : "Preview change-order invoice · send to customer"}</DialogTitle>
          <DialogDescription>{zh ? "左侧为变更单英文 Invoice（含汇款信息，可下载）；右侧话术可修改。确认无误后一键发送短信 / 邮件（邮件附 PDF），记入收款沟通记录。收款到账后仍由财务在变更订单页「确认收款」。" : "Preview / download the change-order invoice, edit the message, then send via SMS / email (PDF attached). Finance still confirms the payment on the change-order page."}</DialogDescription>
        </DialogHeader>
        {err && <p role="alert" className="text-sm text-red-600" data-testid="co-collect-error">{err}</p>}
        {pv && (
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <FileText className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-800">Invoice {pv.invoiceNumbers}</span>
                <span className="text-sm font-bold text-indigo-700 tabular-nums" data-testid="co-collect-total">{fmtMoney(pv.total)}</span>
                {pv.dueDate && <span className="text-xs text-slate-500">{zh ? "到期" : "Due"} {fmtD(pv.dueDate)}</span>}
                <Button size="sm" variant="outline" className="ml-auto" disabled={!pdfUrl} onClick={download} data-testid="co-collect-download"><Download className="h-3.5 w-3.5 mr-1" />{zh ? "下载 PDF" : "Download PDF"}</Button>
              </div>
              <ul className="text-xs text-slate-600 space-y-0.5" data-testid="co-collect-items">
                {pv.items.map((p) => <li key={p.key} data-testid={`co-collect-item-${p.coId}`}>• {(zh ? p.zh : p.en) || p.key} · {zh ? "待收余额" : "Outstanding"} <b>{fmtMoney(p.outstandingAmount ?? p.amount)}</b>{(p.receivedAmount ?? p.collected) > 0 ? <span className="text-slate-500"> · {zh ? "已收" : "Received"} {fmtMoney(p.receivedAmount ?? p.collected)}</span> : null}{p.overdue ? <span className="text-red-600"> · {zh ? "已逾期" : "overdue"} {p.overdueDays}{zh ? "天" : "d"}</span> : null}<CollectionFollowupStatus item={p} zh={zh} compact /></li>)}
              </ul>
              {pdfUrl ? <iframe title="co-invoice" src={`${pdfUrl}#toolbar=0&view=FitH`} style={mx.frameStyle(300)} className="w-full h-[420px] lg:h-[560px] rounded-lg border border-slate-200 bg-slate-50" data-testid="co-collect-pdf-frame" /> : <div className="h-[420px] rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-400" data-testid="co-collect-pdf-loading">{zh ? "正在生成 Invoice…" : "Generating invoice…"}</div>}
            </div>
            <div className="space-y-3">
              <p className="rounded-lg border border-brand-100 bg-brand-50/40 p-3 text-xs leading-relaxed text-slate-600">{zh ? "成功发送后，同一期、同一通道 7 个工作日内暂不重复提醒。7 个工作日升级市场部经理，10 个工作日升级销售 VP 与工程团队。" : "A successful channel has a 7-business-day cooldown. Follow-up escalates to the marketing manager at 7 business days, and sales VP and engineering at 10."}</p>
              <div className="space-y-2">
                {channel({ k: "sms", icon: MessageSquare, label: zh ? "短信" : "SMS", to: pv.to?.phone, avail: pv.channels?.sms, configured: pv.channels?.smsConfigured })}
                {channel({ k: "email", icon: Mail, label: zh ? "邮件（附 Invoice PDF）" : "Email (PDF attached)", to: pv.to?.email, avail: pv.channels?.email, configured: pv.channels?.emailConfigured })}
              </div>
              {ch.email && <label className="block"><span className="text-xs text-slate-500">{zh ? "邮件主题" : "Email subject"}</span><input className={inp} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="co-collect-subject" /></label>}
              <label className="block">
                <span className="text-xs text-slate-500">{zh ? "提醒话术（英文为主；业主为中文时已附中文，可修改）" : "Message (English; Chinese appended for Chinese-speaking owners)"}</span>
                <textarea className={`${inp} font-mono text-xs leading-relaxed`} rows={12} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="co-collect-message" />
                <span className="text-[10px] text-slate-400">{msg.length} {zh ? "字符" : "chars"}{pv.zh ? ` · ${zh ? "含中文" : "bilingual"}` : ""}</span>
              </label>
              <CollectionDeliveryResult result={done} zh={zh} testId="co-collect-result" />
              <div className="flex-wrap md:flex-nowrap flex gap-2 justify-end pt-1">
                <Button variant="outline" size="sm" onClick={onClose} data-testid="co-collect-cancel">{done?.ok ? (zh ? "关闭" : "Close") : (zh ? "取消" : "Cancel")}</Button>
                <Button size="sm" disabled={busy || !msg.trim() || !Object.keys(ch).some(k => ch[k] && !blocked(k))} onClick={send} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="co-collect-send"><Send className="h-3.5 w-3.5 mr-1" />{busy ? (zh ? "发送中…" : "Sending…") : done ? (zh ? "补发未成功通道" : "Retry unsuccessful channels") : (zh ? "确认无误，发送" : "Confirm & send")}</Button>
              </div>
            </div>
          </div>
        )}
        {!pv && !err && <p className="text-sm text-slate-400 py-8 text-center" data-testid="co-collect-loading">{zh ? "加载中…" : "Loading…"}</p>}
      </DialogContent>
    </Dialog>
  );
}
